/**
 * The ONE prequalification-invite status vocabulary.
 *
 * <p><b>Why this lives in `design-system/lib` rather than beside either
 * consumer.</b> Three surfaces render invite rows — the staff list page, the
 * partner-record sections, and the portal tab — and the portal may only import
 * from the design system (`portal/isolation.test.ts`). A module under `@/api`
 * or a page directory is therefore unreachable from one third of the callers,
 * which is exactly why the predicates and badge maps below were copy-pasted
 * three times in the first place. This file is pure data + pure functions: no
 * React, no state, no network, nothing that could smuggle a staff query into a
 * portal bundle.</p>
 *
 * <p><b>What this deliberately is NOT.</b> It is a vocabulary, not a shared
 * component. The three surfaces answer different questions against different
 * scopes — unscoped cross-book, brand-narrowed, and entitlement-scoped — and a
 * merged component would put the staff drafts query one prop away from a
 * partner-facing render. Sharing the words is safe; sharing the reader is not.</p>
 */

/**
 * The EFFECTIVE status: the persisted lifecycle with derived expiry folded in.
 * Mirrors `PrequalInviteEffectiveStatus` (C#).
 *
 * <p>This is the single canonical union. The portal previously carried its own
 * seven-member mirror omitting `Unavailable`, so the same-looking predicate
 * compiled against two different domains. Both wire types now re-export this
 * one, which is what keeps the maps below exhaustive-by-type — a new status
 * becomes a compile error rather than a blank cell.</p>
 */
export type PrequalInviteStatus =
  | "Sent"
  | "Opened"
  | "Started"
  | "Completed"
  | "Revoked"
  | "Expired"
  /** Closed out because the buyer completed a DIFFERENT invite, or an organic
   *  submission, for the same email on the same site. Terminal, and NOT
   *  "completed" — this invite carries no submission of its own. */
  | "ClosedBySibling"
  /** The link cannot be used and the reason is deliberately not stated. Today
   *  that means the sending broker has been off-ramped; the vagueness is the
   *  point, since the value reaches the anonymous public funnel. Derived at
   *  token resolution only — it never appears on a list row. */
  | "Unavailable";

/**
 * Terminal: the invite can never be resent or revoked, and no action will
 * revive it.
 *
 * <p><b>`Expired` is deliberately NOT terminal.</b> A resend pushes the horizon
 * out and the link in the recipient's inbox starts working again — that is the
 * feature, not a loophole, and the server's `ResendAsync` supports it.</p>
 *
 * <p><b>`ClosedBySibling` IS terminal, and its omission was a live defect.</b>
 * It was added as a terminal status without updating the hand-rolled copies of
 * this predicate, so all three surfaces offered enabled Resend and Revoke
 * buttons on an invite the buyer had already superseded — and the server, whose
 * guards had the same omission, honoured them. Resending mailed a live
 * prequalification link to someone who had already finished.</p>
 *
 * <p><b>`Unavailable` is terminal here as a fail-closed default.</b> It never
 * reaches a list row (it is derived per request from the sending broker's
 * status, a join no list read performs), so this arm is unreachable in
 * practice — but if it ever did surface, offering actions on a link the server
 * has decided is unusable is the wrong direction to be wrong in.</p>
 */
export function isTerminalInviteStatus(status: PrequalInviteStatus): boolean {
  switch (status) {
    case "Completed":
    case "Revoked":
    case "ClosedBySibling":
    case "Unavailable":
      return true;
    case "Sent":
    case "Opened":
    case "Started":
    case "Expired":
      return false;
  }
}

/**
 * Outstanding: still awaiting the BUYER's action, and therefore still worth
 * chasing.
 *
 * <p>Distinct from "not terminal": an `Expired` invite is not terminal (a
 * resend revives it) but nobody is waiting on the buyer, because they cannot
 * act until someone resends. This is the set the stall indicator considers.</p>
 */
export function isOutstandingInviteStatus(status: PrequalInviteStatus): boolean {
  switch (status) {
    case "Sent":
    case "Opened":
    case "Started":
      return true;
    case "Completed":
    case "Revoked":
    case "ClosedBySibling":
    case "Expired":
    case "Unavailable":
      return false;
  }
}

/**
 * Statuses offered as filter options on a staff/partner invite list.
 *
 * <p>`Unavailable` is deliberately absent, matching the server: it is not a
 * persisted status, so filtering for it would require joining the sending
 * broker on every list read to serve a filter that would never match a row the
 * list can produce. `ClosedBySibling` IS offered — the server arm for it
 * already existed and only the option list was missing.</p>
 *
 * <p>Note the server's status switch falls through to "return everything" on an
 * unrecognised value, so an option added here without a matching server arm
 * silently disables filtering rather than erroring.</p>
 */
export const PREQUAL_INVITE_FILTER_STATUSES: readonly PrequalInviteStatus[] = [
  "Sent",
  "Opened",
  "Started",
  "Completed",
  "ClosedBySibling",
  "Expired",
  "Revoked",
];

/** Staff `Badge` vocabulary. Was three verbatim copies. */
export const PREQUAL_INVITE_STATUS_BADGE: Record<
  PrequalInviteStatus,
  "draft" | "info" | "gold" | "finalized" | "archived" | "review"
> = {
  Sent: "draft",
  Opened: "info",
  Started: "gold",
  Completed: "finalized",
  // Terminal, but a benign ending — the buyer finished, just not through this
  // link — so it reads as archived rather than as a warning.
  ClosedBySibling: "archived",
  Expired: "review",
  Revoked: "archived",
  // Unreachable on a list row (see the union). Present because the map is
  // exhaustive over the union, which is what forces an entry for a new status
  // rather than letting it render blank.
  Unavailable: "archived",
};

/** Portal `StatusBadge` tone vocabulary — a different scale, same union. */
export const PREQUAL_INVITE_STATUS_TONE: Record<
  PrequalInviteStatus,
  "neutral" | "progress" | "info" | "warn" | "ok" | "danger" | "muted" | "onyx"
> = {
  Sent: "info",
  Opened: "progress",
  Started: "progress",
  Completed: "ok",
  // The buyer finished — through a different link, but finished. Muted rather
  // than "ok": nothing here needs the partner's attention, and showing it as a
  // success beside a real completion would double-count the outcome.
  ClosedBySibling: "muted",
  Revoked: "muted",
  Expired: "warn",
  Unavailable: "muted",
};

/**
 * Why a terminal invite's actions are disabled, for the tooltip on the
 * disabled control. Disabled with no explanation is a dead end; this is what
 * makes it acceptable.
 */
export function terminalInviteReason(status: PrequalInviteStatus): string | null {
  switch (status) {
    case "Completed":
      return "This invitation was completed, so it can't be resent or revoked.";
    case "Revoked":
      return "This invitation was revoked. Send a new one instead.";
    case "ClosedBySibling":
      return "This person already prequalified through another invitation, so this one was closed automatically.";
    case "Unavailable":
      return "This invitation is no longer usable.";
    case "Sent":
    case "Opened":
    case "Started":
    case "Expired":
      return null;
  }
}
